import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Provider } from 'react-redux';
import { store } from './store/store';
import { ToastProvider } from './components/common/Toast';
import { useCartLoader } from './hooks/useCartLoader';
import { useWishlistLoader } from './hooks/useWishlistLoader';
import { useAuth } from './hooks/useAuth';

// Auth Pages
import Login from './pages/auth/Login';
import Register from './pages/auth/Register';
import ForgotPassword from './pages/auth/ForgotPassword';
import ResetPassword from './pages/auth/ResetPassword';
import VerifyOTP from './pages/seller/onboarding/VerifyOTP';

// Public Pages
import PublicHome from './pages/public/Home';
import About from './pages/public/About';
import Contact from './pages/public/Contact';
import ReturnPolicy from './pages/public/refund';
import PublicRentals from './pages/public/Rentals';
import PublicBarter from './pages/public/Barter';

// Removed customer Products import, since it moved to Home
import ProductDetails from './pages/customer/ProductDetails';
import CategoryProducts from './pages/customer/CategoryProducts';
import SellerProfile from './pages/customer/SellerProfile';

// Customer Pages
import CustomerDashboard from './pages/customer/Dashboard';
import Cart from './pages/customer/Cart';
import Wishlist from './pages/customer/Wishlist';
import Following from './pages/customer/Following';
import CustomerProfile from './pages/customer/Profile';
import CustomerSettings from './pages/customer/Settings';
import Addresses from './pages/customer/Addresses';
import Checkout from './pages/customer/Checkout';
import OrderSuccess from './pages/customer/OrderSuccess';
import Orders from './pages/customer/Orders';
import OrderDetails from './pages/customer/OrderDetails';



// Seller Pages
import SellerDashboard from './pages/seller/Dashboard';
import SellerProducts from './pages/seller/Products';
import ProductCreate from './pages/seller/ProductCreate';
import ProductEdit from './pages/seller/ProductEdit';
import SellerProductDetails from './pages/seller/ProductDetails';
import SellerProfile2 from './pages/seller/Profile';
import SellerSettings from './pages/seller/Settings';
import Followers from './pages/seller/Followers';
import Subscription from './pages/seller/Subscription';
import SubscriptionHistory from './pages/seller/SubscriptionHistory';
import SellerOrders from './pages/seller/Orders';
import SellerOrderDetails from './pages/seller/OrderDetails';
import OnboardingStatus from './pages/seller/onboarding/OnboardingStatus';
import CompleteProfile from './pages/seller/onboarding/CompleteProfile';
import RentalList from './pages/seller/RentalList';
import RentalCreate from './pages/seller/RentalCreate';
import BarterList from './pages/seller/BarterList';
import BarterCreate from './pages/seller/BarterCreate';
import BarterExchangeRequests from './pages/seller/BarterExchangeRequests';

// Admin Pages
import AdminDashboard from './pages/admin/Dashboard';
import Categories from './pages/admin/Categories';
import AdminProducts from './pages/admin/Products';
import AdminProductDetails from './pages/admin/ProductDetails';
import PendingSellers from './pages/admin/PendingSellers';
import SellerManagement from './pages/admin/SellerManagement';
import SellerApplicationDetail from './pages/admin/SellerApplicationDetail';
import AdminProfile from './pages/admin/Profile';
import AdminSettings from './pages/admin/Settings';
import AdminOrders from './pages/admin/Orders';
import AdminOrderDetails from './pages/admin/OrderDetails';
import AdminSubscriptions from './pages/admin/Subscriptions';
import ContactMessages from './pages/admin/ContactMessages';
import AdminRefunds from './pages/admin/Refunds';
import AdManagement from './pages/admin/AdManagement';

// Shared Pages
import Profile from './pages/shared/Profile';
import Settings from './pages/shared/Settings';
import NotFound from './pages/shared/NotFound';

// Route Components
import ProtectedRoute from './components/routes/ProtectedRoute';
import RoleRoute from './components/routes/RoleRoute';
import PublicRoute from './components/routes/PublicRoute';
import AuthLayout from './components/layouts/AuthLayout';
import TermsOfService from './pages/public/terms';
import PrivacyPolicy from './pages/public/privacy';

function AppContent() {
  const { isAuthenticated, role } = useAuth();

  useCartLoader({ enabled: isAuthenticated && role === 'customer' });
  useWishlistLoader({ enabled: isAuthenticated && role === 'customer' });

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<PublicHome />} />
      <Route path="/rentals" element={<PublicRentals />} />
      <Route path="/barter" element={<PublicBarter />} />
      <Route path="/about" element={<About />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/products" element={<Navigate to="/" replace />} />
      <Route path="/products/:slug" element={<ProductDetails />} />
      <Route path="/categories/:slug" element={<CategoryProducts />} />
      <Route path="/sellers/:sellerId" element={<SellerProfile />} />
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="/refund-policy" element={<ReturnPolicy />} />
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />


      {/* Auth Routes */}
      <Route path="/login" element={<PublicRoute><AuthLayout><Login /></AuthLayout></PublicRoute>} />
      <Route path="/register" element={<PublicRoute><AuthLayout><Register /></AuthLayout></PublicRoute>} />
      <Route path="/forgot-password" element={<PublicRoute><AuthLayout><ForgotPassword /></AuthLayout></PublicRoute>} />
      <Route path="/reset-password/:token" element={<PublicRoute><AuthLayout><ResetPassword /></AuthLayout></PublicRoute>} />
      <Route path="/seller/verify-otp" element={<PublicRoute><VerifyOTP /></PublicRoute>} />

      {/* Customer Routes */}
      <Route path="/dashboard" element={
        <RoleRoute allowedRoles={['customer']}><CustomerDashboard /></RoleRoute>
      } />
      <Route path="/cart" element={
        <RoleRoute allowedRoles={['customer']}><Cart /></RoleRoute>
      } />
      <Route path="/wishlist" element={
        <RoleRoute allowedRoles={['customer']}><Wishlist /></RoleRoute>
      } />
      <Route path="/following" element={
        <RoleRoute allowedRoles={['customer']}><Following /></RoleRoute>
      } />
      <Route path="/profile" element={
        <RoleRoute allowedRoles={['customer']}><CustomerProfile /></RoleRoute>
      } />
      <Route path="/settings" element={
        <RoleRoute allowedRoles={['customer']}><CustomerSettings /></RoleRoute>
      } />
      <Route path="/addresses" element={
        <RoleRoute allowedRoles={['customer']}><Addresses /></RoleRoute>
      } />
      <Route path="/orders" element={
        <RoleRoute allowedRoles={['customer']}><Orders /></RoleRoute>
      } />
      <Route path="/orders/:id" element={
        <RoleRoute allowedRoles={['customer']}><OrderDetails /></RoleRoute>
      } />
      <Route path="/checkout" element={
        <RoleRoute allowedRoles={['customer']}><Checkout /></RoleRoute>
      } />
      <Route path="/order-success" element={
        <RoleRoute allowedRoles={['customer']}><OrderSuccess /></RoleRoute>
      } />

      {/* Seller Routes */}
      <Route path="/seller/dashboard" element={
        <RoleRoute allowedRoles={['seller']}><SellerDashboard /></RoleRoute>
      } />
      <Route path="/seller/products" element={
        <RoleRoute allowedRoles={['seller']}><SellerProducts /></RoleRoute>
      } />
      <Route path="/seller/products/create" element={
        <RoleRoute allowedRoles={['seller']}><ProductCreate /></RoleRoute>
      } />
      <Route path="/seller/products/:id/edit" element={
        <RoleRoute allowedRoles={['seller']}><ProductEdit /></RoleRoute>
      } />
      <Route path="/seller/products/:id" element={
        <RoleRoute allowedRoles={['seller']}><SellerProductDetails /></RoleRoute>
      } />
      <Route path="/seller/followers" element={
        <RoleRoute allowedRoles={['seller']}><Followers /></RoleRoute>
      } />
      <Route path="/seller/profile" element={
        <RoleRoute allowedRoles={['seller']}><SellerProfile2 /></RoleRoute>
      } />
      <Route path="/seller/settings" element={
        <RoleRoute allowedRoles={['seller']}><SellerSettings /></RoleRoute>
      } />
      <Route path="/seller/subscription" element={
        <RoleRoute allowedRoles={['seller']}><Subscription /></RoleRoute>
      } />
      <Route path="/seller/subscription/history" element={
        <RoleRoute allowedRoles={['seller']}><SubscriptionHistory /></RoleRoute>
      } />
      <Route path="/seller/orders" element={
        <RoleRoute allowedRoles={['seller']}><SellerOrders /></RoleRoute>
      } />
      <Route path="/seller/orders/:id" element={
        <RoleRoute allowedRoles={['seller']}><SellerOrderDetails /></RoleRoute>
      } />
      <Route path="/seller/onboarding/status" element={
        <RoleRoute allowedRoles={['seller']}><OnboardingStatus /></RoleRoute>
      } />
      <Route path="/seller/onboarding/complete-profile" element={
        <RoleRoute allowedRoles={['seller']}><CompleteProfile /></RoleRoute>
      } />
      <Route path="/seller/rentals" element={
        <RoleRoute allowedRoles={['seller']}><RentalList /></RoleRoute>
      } />
      <Route path="/seller/rentals/create" element={
        <RoleRoute allowedRoles={['seller']}><RentalCreate /></RoleRoute>
      } />
      <Route path="/seller/barter" element={
        <RoleRoute allowedRoles={['seller']}><BarterList /></RoleRoute>
      } />
      <Route path="/seller/barter/create" element={
        <RoleRoute allowedRoles={['seller']}><BarterCreate /></RoleRoute>
      } />
      <Route path="/seller/barter/:id/requests" element={
        <RoleRoute allowedRoles={['seller']}><BarterExchangeRequests /></RoleRoute>
      } />

      {/* Admin Routes */}
      <Route path="/admin/dashboard" element={
        <RoleRoute allowedRoles={['admin']}><AdminDashboard /></RoleRoute>
      } />
      <Route path="/admin/categories" element={
        <RoleRoute allowedRoles={['admin']}><Categories /></RoleRoute>
      } />
      <Route path="/admin/products" element={
        <RoleRoute allowedRoles={['admin']}><AdminProducts /></RoleRoute>
      } />
      <Route path="/admin/products/:id" element={
        <RoleRoute allowedRoles={['admin']}><AdminProductDetails /></RoleRoute>
      } />
      <Route path="/admin/sellers/pending" element={
        <RoleRoute allowedRoles={['admin']}><PendingSellers /></RoleRoute>
      } />
      <Route path="/admin/sellers/all" element={
        <RoleRoute allowedRoles={['admin']}><SellerManagement /></RoleRoute>
      } />
      <Route path="/admin/sellers/:id" element={
        <RoleRoute allowedRoles={['admin']}><SellerApplicationDetail /></RoleRoute>
      } />
      <Route path="/admin/profile" element={
        <RoleRoute allowedRoles={['admin']}><AdminProfile /></RoleRoute>
      } />
      <Route path="/admin/settings" element={
        <RoleRoute allowedRoles={['admin']}><AdminSettings /></RoleRoute>
      } />
      <Route path="/admin/contact-messages" element={
        <RoleRoute allowedRoles={['admin']}><ContactMessages /></RoleRoute>
      } />
      <Route path="/admin/ads" element={
        <RoleRoute allowedRoles={['admin']}><AdManagement /></RoleRoute>
      } />
      <Route path="/admin/orders" element={
        <RoleRoute allowedRoles={['admin']}><AdminOrders /></RoleRoute>
      } />
      <Route path="/admin/orders/:id" element={
        <RoleRoute allowedRoles={['admin']}><AdminOrderDetails /></RoleRoute>
      } />
      <Route path="/admin/refunds" element={
        <RoleRoute allowedRoles={['admin']}><AdminRefunds /></RoleRoute>
      } />
      <Route path="/admin/subscriptions" element={
        <RoleRoute allowedRoles={['admin']}><AdminSubscriptions /></RoleRoute>
      } />

      {/* Shared Protected Routes */}
      <Route path="/account/profile" element={
        <ProtectedRoute><Profile /></ProtectedRoute>
      } />
      <Route path="/account/settings" element={
        <ProtectedRoute><Settings /></ProtectedRoute>
      } />

      {/* 404 */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

function App() {
  return (
    <Provider store={store}>
      <ToastProvider>
        <BrowserRouter>
          <AppContent />
        </BrowserRouter>
      </ToastProvider>
    </Provider>
  );
}

export default App;